import * as api from "@/api";
import QueryDataManager from "@/classes/query-data-manager";
import styles from "@/components/pinned-notes.module.css";
import useNotes from "@/hooks/use-notes";
import * as signals from "@/signals";
import { handleKeyboardClick, openNote, showMenu } from "@/utils";
import { faThumbTackSlash, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import dayjs from "dayjs";
import { t } from "i18next";
import { JSX, MouseEvent } from "react";

function PinnedNotes(): JSX.Element {
	const queryClient = useQueryClient();

	const { data } = useNotes();

	const handleContextMenu = (event: MouseEvent<HTMLDivElement>): void => {
		showMenu(event, [
			{
				content: {
					icon: faThumbTackSlash,
					text: t("unpin"),
				},
				onClick: (): void => {},
				when: true,
			},
		]);
	};

	const pinnedNotes = data?.results.filter((note) => note.isPinned);
	pinnedNotes?.sort((a, b) => {
		const aPinnedAt = a.pinnedAt || a.createdAt;
		const bPinnedAt = b.pinnedAt || b.createdAt;
		return new Date(bPinnedAt).getTime() - new Date(aPinnedAt).getTime();
	});
	const noteElements = pinnedNotes?.map((note, index) => {
		const handleOpenClick = (): void => {
			openNote(note);
		};

		const handleUnpinClick = async (
			event: MouseEvent<HTMLButtonElement>,
		): Promise<void> => {
			event.stopPropagation();
			signals.isLoadingShown.value = true;
			try {
				await new QueryDataManager(queryClient).unpinNote(note);
			} catch (error) {
				void api.handleApiError(error);
			} finally {
				signals.isLoadingShown.value = false;
			}
		};

		let noteContent = note.content;

		// Handle date countdowns
		try {
			const toTimeMatches = noteContent.match(
				/\[toTime\s\d{4}(\/|-|\.)\d{1,2}\1\d{1,2}\]/gi,
			);
			if (toTimeMatches) {
				for (const match of toTimeMatches) {
					const targetTime = match
						.replace(/\[toTime\s|\]/gi, "")
						.replace(/-|\./g, "-");
					const targetDate = dayjs(targetTime);
					const today = dayjs();
					const diff = Math.abs(today.diff(targetDate, "day"));
					let description: string;
					if (today.isAfter(targetDate)) {
						description = t("daysAgo", { days: diff });
					} else if (today.isSame(targetDate, "day")) {
						description = t("isToday");
					} else {
						description = t("daysLeft", { days: diff });
					}
					noteContent = noteContent.replace(match, description);
				}
			}
		} catch (error) {
			console.error(error);
		}

		return (
			<div
				className={clsx(
					styles["pinned-note"],
					!signals.generalSettings.value
						.shouldShowPinnedNoteCompleteContent &&
						styles["one-line"],
				)}
				key={note.id}
				onClick={handleOpenClick}
				onContextMenu={handleContextMenu}
				onKeyDown={handleKeyboardClick(handleOpenClick)}
				role="button"
				style={{ animationDelay: `${index * 0.1}s` }}
				tabIndex={0}
			>
				{noteContent}
				<div className={styles["note-time"]}>
					{new Date(note.modifiedAt).toLocaleString(undefined, {
						day: "numeric",
						hour: "2-digit",
						minute: "2-digit",
						month: "numeric",
						year: "numeric",
					})}
				</div>
				<button
					className={styles["close-button"]}
					onClick={(event) => void handleUnpinClick(event)}
					title={t("unpin")}
					type="button"
				>
					<div className={styles["close-button-inner"]}>
						<FontAwesomeIcon icon={faXmark} />
					</div>
				</button>
			</div>
		);
	});

	return <div className={styles["container"]}>{noteElements}</div>;
}

export default PinnedNotes;
